import { RoomBaseData, RoomExtraData } from '@/editor/data/room/Room';
import WallStore from './WallStore';
import { ERoomType } from '../data/type';
import i18next from '@/i18n';
import { getUniqueId } from '../utils';
import { matchRoomsByCorePath } from '../utils/room/match';
import { IWallEdge } from '../types';
import { pointEqual } from '../math/utils';
import { globalStores, TGlobalStores } from '.';
import { EFaceType } from '../entities/type';
import { BaseRoomStore } from './base/baseRoomStore';

// 壁芯环是否一字不差。用来决定要不要往 base 回写 —— refreshData 在「改房间名/换材质」这类
// 完全不动几何的操作里也会跑一遍，无脑回写会给撤销栈塞一串内容相同的 patch，
// 每条都带一份克隆的边数组。
function isSameCorePath(a: IWallEdge[], b: IWallEdge[]) {
  if (a.length !== b.length) return false;
  return a.every((edge, index) => {
    const other = b[index];
    if (edge.wallId !== other.wallId || edge.side !== other.side) return false;
    if (!pointEqual(edge.start, other.start) || !pointEqual(edge.end, other.end)) return false;
    if (edge.clockwise !== other.clockwise) return false;
    if (!edge.center || !other.center) return !edge.center && !other.center;
    return pointEqual(edge.center, other.center);
  });
}

type IRefreshRoomDatasParams = {
  pathInfos: IWallEdge[];
  holeInfos: IWallEdge[][];
  corePathInfos: IWallEdge[];
  coreHoleInfos: IWallEdge[][];
}[];

class RoomStore extends BaseRoomStore {
  private _wallStore: WallStore;

  constructor(gs: TGlobalStores) {
    super(gs);
    this._wallStore = gs.wallStore;
  }

  getRoomData(id: string) {
    return this.get(id);
  }

  getRoomDatas() {
    return this.getList();
  }

  updateRoomBaseData(roomId: string, newBaseData: DeepPartial<RoomBaseData>) {
    this.updateBaseData(roomId, newBaseData);
  }

  updateRoomExtraData(roomId: string, newExtraData: RoomExtraData) {
    this.updateExtraData(roomId, newExtraData);
  }

  clearAll() {
    this.clear();
  }

  refreshRoomDatas(areaResult: IRefreshRoomDatasParams) {
    const roomDatas = this.getRoomDatas();
    const deleteRoomSet: Set<string> = new Set(roomDatas.map((roomData) => roomData.id)); // 要删除的roomid

    // 新区域 → 旧房间：先按壁芯边(wallId+side)序列精确匹配，对不上的再按壁芯轮廓重合度兜底认领。
    // 旧版本存下来的 corePathInfos 不含「墙搭在房间边界上、轮廓要沿它出去再绕回来」这类边
    // (本例：大房间少了绕小房间那一圈，8 条边 vs 14 条边)，序列 key 天然对不上，但描述的是同一间房；
    // 没有兜底它会被删掉重建成新 id 的房间 —— 材质/名称/offGround 回默认，分区被清、隔墙关联变孤儿。
    // 认领必须拿 base 表里的壁芯环：getRoomDatas() 拼出来的那份是 extra 盖在 base 上的，而 extra
    // 不进 history —— 撤销后它停留在撤销前那一版（可能含已经被撤销掉的墙 id，兜底轮的
    // 「旧边集 ⊆ 新边集」就永远过不了），房间只能删掉重建，名称/材质/offGround 全丢。
    const matchCandidates = roomDatas.map((roomData) => ({
      id: roomData.id,
      corePathInfos: this.getBaseData(roomData.id)?.corePathInfos ?? roomData.corePathInfos
    }));
    const matchResults = matchRoomsByCorePath(areaResult, matchCandidates);

    areaResult.forEach((areaInfo, index) => {
      const pathWallIds = areaInfo.corePathInfos.map((edge) => edge.wallId);
      // const holeWallIds = areaInfo.coreHoleInfos.flatMap((edges) => edges.map((edge) => edge.wallId));
      // const relateWallIds = Array.from(new Set([...pathWallIds, ...holeWallIds]));

      const heights = pathWallIds.flatMap((wallId) => {
        // 壁芯边上的墙 id 必然在 store 里；取不到的不参与取最大高度
        const wallData = this._wallStore.getWallData(wallId);
        return wallData ? [wallData.baseData.height] : [];
      });

      // 更新/新增房间
      const { roomId: oldRoomId, exact } = matchResults[index];
      if (oldRoomId) {
        deleteRoomSet.delete(oldRoomId);

        // 兜底认领的房间，边界与旧数据本来就不是同一串壁芯边；relateWallIds 只在 add 时写过一次、
        // 之后再没更新过，这里补上，否则它会一直停留在创建那一刻的旧墙表。
        if (!exact) {
          this.updateRoomBaseData(oldRoomId, { relateWallIds: Array.from(new Set(pathWallIds)) });
        }

        // 壁芯环变了就同步给 base 那一份（下次认领的 key，理由见 updateBaseCorePathInfos）
        const oldCorePathInfos = this.getBaseData(oldRoomId)?.corePathInfos;
        if (!oldCorePathInfos || !isSameCorePath(oldCorePathInfos, areaInfo.corePathInfos)) {
          this.updateBaseCorePathInfos(oldRoomId, areaInfo.corePathInfos);
        }

        this.updateRoomExtraData(oldRoomId, {
          pathInfos: areaInfo.pathInfos,
          holeInfos: areaInfo.holeInfos,
          corePathInfos: areaInfo.corePathInfos,
          coreHoleInfos: areaInfo.coreHoleInfos,
          height: Math.max(...heights)
        });
      } else {
        this.add({
          id: getUniqueId(),
          offGround: 0,
          // 房间的wallDatas与wallstore里面的wallDatas没有关系，最多是数据一样而已，id是不一样的
          // wallDatas: [],
          // 房间的relateWallDatas与wallstore里面的wallDatas是一样的
          relateWallIds: pathWallIds,
          pathInfos: areaInfo.pathInfos,
          holeInfos: areaInfo.holeInfos,
          corePathInfos: areaInfo.corePathInfos,
          coreHoleInfos: areaInfo.coreHoleInfos,
          height: Math.max(...heights),
          roomInfo: { type: ERoomType.Default, name: i18next.t('draw.house.parameters.unname') },
          materialInfo: globalStores.getDefaultMaterial({ type: EFaceType.FloorFace })
        });
      }
    });

    // 获取要删除的房间数据
    deleteRoomSet.forEach((roomId) => {
      this.remove(roomId);
    });
  }

  getRoomRelativeWallDatas(roomId: string) {
    const { relateWallIds } = this.getRoomData(roomId) ?? {};
    if (!relateWallIds || relateWallIds.length === 0) return [];
    return this._wallStore.getWallDatas().filter(({ id }) => relateWallIds.includes(id));
  }

  /** 入参可能是完整的 RoomData（新存档），也可能只有基础字段（老存档），故派生字段按可选处理 */
  coverDatas(roomDataBases: (RoomBaseData & Partial<RoomExtraData>)[]) {
    this.clearAll();

    for (const rb of roomDataBases) {
      // 壁芯是认领旧房间的 key，清空会让所有房间的 key 塌成空串、全部删掉重建，名称与材质全丢。
      // 更老的存档没有这个字段，兜底成空数组，认领轮会自动跳过。与 RegionStore.coverDatas 一致。
      const { corePathInfos = [] } = rb;
      const roomData = {
        ...rb,
        height: 0,
        pathInfos: [],
        holeInfos: [],
        corePathInfos,
        coreHoleInfos: []
      };
      this.add(roomData);
    }
  }
}

export default RoomStore;
